import { InfoOutlined as InfoOutlinedIcon, SearchOutlined } from 'components/icons';
import LazyTooltip from 'components/LazyTooltip';
import { InputGroup, InputGroupAddon, InputGroupInput } from 'components/ui/input-group';
import { ComponentProps, useEffect, useMemo, useRef } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { HighlightSpan, tokenizeWithSpans } from 'utils/searchQueryParser';

const SEARCH_TIPS = [
    'search-tips.phrase',
    'search-tips.and',
    'search-tips.or',
    'search-tips.not',
    'search-tips.wildcard-star',
    'search-tips.wildcard-question',
];

const SearchTip = ({ tipKey }: { tipKey: string }) => (
    <div>
        <div className="text-caption">
            <b>{t(`${tipKey}.operator`)}</b>: <i>{t(`${tipKey}.description`)}</i>
        </div>
        <div className="text-caption text-grey-300">{t(`${tipKey}.example`)}</div>
    </div>
);

const SearchTipsTooltipContent = () => (
    <div className="flex max-w-[400px] flex-col gap-1.5 p-1">
        <span className="text-caption font-bold">{t('search-tips.title')}</span>
        <span className="text-caption">{t('search-tips.description')}</span>
        {SEARCH_TIPS.map((key) => (
            <SearchTip key={key} tipKey={key} />
        ))}
    </div>
);

/** The text box of the field; the highlighting mirror copies it exactly so its glyphs sit under the input's. */
const FIELD_CLASS = 'h-full w-full min-w-0 px-1.5 text-base outline-none md:text-sm';

// Chip-like background with negative margin so the painted background extends visually beyond the
// text without changing layout width — mirror chars stay aligned with the underlying transparent
// <input>'s caret positions.
const CHIP = 'rounded-[4px] -mx-0.5 px-0.5';
const OPERATOR_CLASS = cn(CHIP, 'bg-primary/16 text-primary font-semibold');
const PHRASE_CLASS = cn(CHIP, 'bg-success/16');
const WILDCARD_WORD_CLASS = cn(CHIP, 'bg-warning/18');
const WILDCARD_CHAR_CLASS = 'text-warning-darker font-semibold';

/**
 * The search input with the query's syntax highlighted: the real `<input>` draws a transparent text
 * and the caret, and a mirror behind it draws the same text with operators, phrases and wildcards
 * marked. The mirror follows the input's horizontal scroll.
 */
function HighlightedInput({ value, className, ...rest }: ComponentProps<'input'>) {
    const inputRef = useRef<HTMLInputElement>(null);
    const mirrorRef = useRef<HTMLDivElement>(null);
    const text = typeof value === 'string' ? value : value != null ? String(value) : '';
    const spans = useMemo(() => tokenizeWithSpans(text), [text]);

    useEffect(() => {
        const input = inputRef.current;
        const mirror = mirrorRef.current;
        if (!input || !mirror) return;
        const sync = () => {
            mirror.scrollLeft = input.scrollLeft;
        };
        sync();
        input.addEventListener('scroll', sync, { passive: true });
        input.addEventListener('input', sync);
        input.addEventListener('keyup', sync);
        input.addEventListener('click', sync);
        const observer = new ResizeObserver(sync);
        observer.observe(input);
        return () => {
            input.removeEventListener('scroll', sync);
            input.removeEventListener('input', sync);
            input.removeEventListener('keyup', sync);
            input.removeEventListener('click', sync);
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        const input = inputRef.current;
        const mirror = mirrorRef.current;
        if (input && mirror) mirror.scrollLeft = input.scrollLeft;
    }, [text]);

    // Group consecutive TERM/WILDCARD spans (always part of the same word — whitespace
    // would emit a separating WHITESPACE span) so a word like `baz*` renders under one
    // shared background, with `*` getting its own foreground accent.
    type RenderUnit =
        | { kind: 'single'; span: HighlightSpan }
        | { kind: 'word'; spans: HighlightSpan[]; hasWildcard: boolean };
    const units: RenderUnit[] = [];
    for (let i = 0; i < spans.length; i++) {
        const span = spans[i];
        if (span.type === 'TERM' || span.type === 'WILDCARD') {
            const wordSpans: HighlightSpan[] = [];
            let hasWildcard = false;
            while (i < spans.length && (spans[i].type === 'TERM' || spans[i].type === 'WILDCARD')) {
                if (spans[i].type === 'WILDCARD') hasWildcard = true;
                wordSpans.push(spans[i]);
                i++;
            }
            i--;
            units.push({ kind: 'word', spans: wordSpans, hasWildcard });
        } else {
            units.push({ kind: 'single', span });
        }
    }

    return (
        <div className="relative flex min-w-0 flex-1 self-stretch">
            <input
                {...rest}
                ref={inputRef}
                value={value}
                data-slot="input-group-control"
                className={cn(FIELD_CLASS, 'caret-foreground relative z-1 bg-transparent text-transparent', className)}
            />
            <div
                ref={mirrorRef}
                aria-hidden
                className={cn(
                    FIELD_CLASS,
                    'text-foreground pointer-events-none absolute inset-0 flex items-center overflow-hidden whitespace-pre'
                )}
            >
                {units.map((unit) => {
                    if (unit.kind === 'single') {
                        const { span } = unit;
                        const slice = text.slice(span.start, span.end);
                        const unitKey = `${span.start}-${span.end}`;
                        if (span.type === 'OPERATOR') {
                            return (
                                <span key={unitKey} className={OPERATOR_CLASS}>
                                    {slice}
                                </span>
                            );
                        }
                        if (span.type === 'PHRASE') {
                            return (
                                <span key={unitKey} className={PHRASE_CLASS}>
                                    {slice}
                                </span>
                            );
                        }
                        return <span key={unitKey}>{slice}</span>;
                    }
                    const unitStart = unit.spans[0].start;
                    const unitEnd = unit.spans[unit.spans.length - 1].end;
                    const unitKey = `${unitStart}-${unitEnd}`;
                    if (!unit.hasWildcard) {
                        return <span key={unitKey}>{text.slice(unitStart, unitEnd)}</span>;
                    }
                    return (
                        <span key={unitKey} className={WILDCARD_WORD_CLASS}>
                            {unit.spans.map((s) => (
                                <span
                                    key={`${s.start}-${s.end}`}
                                    className={s.type === 'WILDCARD' ? WILDCARD_CHAR_CLASS : undefined}
                                >
                                    {text.slice(s.start, s.end)}
                                </span>
                            ))}
                        </span>
                    );
                })}
            </div>
        </div>
    );
}

export type Props = Omit<ComponentProps<'input'>, 'size' | 'className'> & {
    count?: number | string;
    singularTextId?: string;
    pluralTextId?: string;
    placeHolder?: string;
    isInViewMode?: boolean;
    showSearchTips?: boolean;
    /** Classes for the field's box — its width, its margin. */
    className?: string;
};

const SearchBar: React.FC<Props> = ({
    count = 0,
    singularTextId = 'entry',
    pluralTextId = 'entries',
    placeHolder,
    isInViewMode,
    showSearchTips = false,
    className,
    ...other
}) => {
    const inputProps = {
        placeholder: placeHolder
            ? placeHolder
            : t('search-records', {
                  record: count === 1 ? t(singularTextId).toLocaleLowerCase() : t(pluralTextId).toLocaleLowerCase(),
              }),
        disabled: isInViewMode,
        ...other,
    };
    return (
        <InputGroup className={cn('bg-paper h-10', className)} data-disabled={isInViewMode || undefined}>
            <InputGroupAddon align="inline-start">
                <SearchOutlined />
            </InputGroupAddon>
            {showSearchTips ? (
                <HighlightedInput {...inputProps} />
            ) : (
                <InputGroupInput {...inputProps} className="h-full px-1.5" />
            )}
            {showSearchTips ? (
                <InputGroupAddon align="inline-end">
                    <LazyTooltip
                        title={<SearchTipsTooltipContent />}
                        placement="bottom"
                        contentClassName="max-w-[450px]"
                    >
                        <InfoOutlinedIcon className="text-grey-500 size-5 cursor-pointer" />
                    </LazyTooltip>
                </InputGroupAddon>
            ) : null}
        </InputGroup>
    );
};

export default SearchBar;
